/**
 * "Give feedback", in the chrome, on every screen.
 *
 * **Placement.** The top bar, beside the theme toggle and sign out. It is the
 * one region of the app that is present on every screen at every width, sits
 * above the page's own banner rather than inside it, and never scrolls away.
 * The sidebar footer was the alternative and fails the requirement twice over:
 * it is `lg:hidden` under the icon rail, and on a phone the whole sidebar is
 * an overlay somebody has to open first, so the affordance would be two
 * presses away exactly when a person is stuck. The label follows the sign-out
 * button's rule below `sm`, where the visible words give way to the icon and
 * the name is restored in text rather than in a `title` nothing reads.
 *
 * **Who sees it.** Everybody signed in, Viewers included. That is J4, and it
 * is a recorded exception to the read-only role rather than an oversight: a
 * beta reviewer locked out of the feedback button defeats the feature, and
 * feedback about the product is not the business data the role guards. There
 * is deliberately no role check in this file at all.
 */

import { MessageSquarePlus } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui"

import { FeedbackDialog, THANK_YOU } from "./feedback-dialog"

export function FeedbackButton({ route }: { route: string }) {
  const [open, setOpen] = React.useState(false)
  const [announcement, setAnnouncement] = React.useState("")

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          // Cleared on the way in, not on the way out: a live region announces
          // what changed, so leaving the previous thanks in place would make
          // the second entry land in silence.
          setAnnouncement("")
          setOpen(true)
        }}
      >
        <MessageSquarePlus className="h-4 w-4" aria-hidden />
        <span className="hidden sm:inline">Give feedback</span>
        {/* Below `sm` the label above is `display: none` and the icon is
            `aria-hidden`, which would leave the button announcing itself as
            nothing at all. The two spans are mutually exclusive at every
            width, so the name is "Give feedback" and never said twice. */}
        <span className="sr-only sm:hidden">Give feedback</span>
      </Button>

      {open && (
        <FeedbackDialog
          // Captured at open time. The dialog is where the person is when they
          // press the button, and a route read later would name whichever
          // screen they navigated to while typing.
          route={route}
          onClose={() => setOpen(false)}
          onSent={() => setAnnouncement(THANK_YOU)}
        />
      )}

      {/* Outside the dialog on purpose: the dialog unmounts a moment after the
          entry lands, and a live region that goes with it can be cut off
          before it has spoken. Polite, and outcome only. */}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </>
  )
}
